import React, { useState } from 'react';
import { BeatLoader } from 'react-spinners';
import { toast } from 'react-hot-toast';
import { Amplify } from 'aws-amplify';
import { resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import awsconfig from '../../aws-exports';
import { Mail, Lock, Key, Send, CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';

Amplify.configure(awsconfig);

const ForgotPassword = ({ username, onClose }) => {
  const [email, setEmail] = useState(username || '');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [loadingRequest, setLoadingRequest] = useState(false);
  const [loadingReset, setLoadingReset] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleRequestCode = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    try {
      setLoadingRequest(true);
      await resetPassword({ username: email });
      setIsCodeSent(true);
      setLoadingRequest(false);
      toast.success('Verification code sent to your email');
    } catch (error) {
      setLoadingRequest(false);
      console.error('Error requesting password reset:', error);
      toast.error(error.message || 'Failed to send verification code');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!verificationCode || !newPassword || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    try {
      setLoadingReset(true);
      await confirmResetPassword({
        username: email,
        confirmationCode: verificationCode,
        newPassword: newPassword
      });
      setLoadingReset(false);
      toast.success('Password reset successfully! You can now sign in with your new password.');
      onClose();
    } catch (error) {
      setLoadingReset(false);
      console.error('Error resetting password:', error);
      toast.error(error.message || 'Failed to reset password');
    }
  };

  const passwordsMatch = newPassword && confirmPassword && newPassword === confirmPassword;
  const passwordValid = newPassword && newPassword.length >= 8;

  return (
    <>
      <style>{`
        .forgot-password-container {
          width: 100%;
          max-width: 400px;
          margin: 0 auto;
        }

        .forgot-form-group {
          margin-bottom: 20px;
          position: relative;
        }

        .forgot-input-container {
          position: relative;
        }

        .forgot-input-field {
          width: 100%;
          height: 48px;
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          padding: 0 16px 0 44px;
          font-size: 16px;
          transition: all 0.3s ease;
          background: #fff;
          color: #1f2937;
          font-family: inherit;
        }

        .forgot-input-field:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
          transform: translateY(-1px);
        }

        .forgot-input-field::placeholder {
          color: #9ca3af;
        }

        .forgot-input-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: #9ca3af;
          transition: all 0.3s ease;
          pointer-events: none;
        }

        .forgot-input-field:focus + .forgot-input-icon {
          color: #667eea;
        }

        .forgot-password-toggle {
          position: absolute;
          right: 14px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: #9ca3af;
          cursor: pointer;
          transition: all 0.3s ease;
          padding: 4px;
          border-radius: 6px;
        }

        .forgot-password-toggle:hover {
          color: #667eea;
          background: rgba(102, 126, 234, 0.1);
        }

        .forgot-btn {
          width: 100%;
          height: 48px;
          background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
          border: none;
          border-radius: 12px;
          color: white;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin-bottom: 20px;
        }

        .forgot-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(139, 92, 246, 0.3);
        }

        .forgot-btn:active {
          transform: translateY(0);
        }

        .forgot-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .info-container {
          background: #f0f9ff;
          border: 2px solid #0ea5e9;
          border-radius: 16px;
          padding: 20px;
          margin-bottom: 24px;
          text-align: center;
        }

        .info-icon {
          margin: 0 auto 12px auto;
          color: #0ea5e9;
        }

        .info-title {
          font-size: 16px;
          font-weight: 600;
          color: #0c4a6e;
          margin: 0 0 8px 0;
        }

        .info-description {
          color: #0369a1;
          font-size: 14px;
          margin: 0;
          line-height: 1.5;
        }

        .info-email {
          font-weight: 600;
          color: #0284c7;
        }

        .reset-container {
          background: #fef3c7;
          border: 2px solid #f59e0b;
          border-radius: 16px;
          padding: 24px;
          margin-bottom: 20px;
        }

        .reset-icon {
          margin: 0 auto 16px auto;
          color: #d97706;
        }

        .reset-title {
          font-size: 18px;
          font-weight: 600;
          color: #92400e;
          margin: 0 0 8px 0;
          text-align: center;
        }

        .reset-description {
          color: #a16207;
          font-size: 14px;
          margin: 0 0 20px 0;
          line-height: 1.5;
          text-align: center;
        }

        .password-validation {
          display: flex;
          gap: 12px;
          margin-top: 8px;
          font-size: 12px;
        }

        .validation-item {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          border-radius: 6px;
          transition: all 0.3s ease;
        }

        .validation-success {
          background: #dcfce7;
          color: #166534;
        }

        .validation-error {
          background: #fef2f2;
          color: #dc2626;
        }

        .validation-neutral {
          background: #f3f4f6;
          color: #6b7280;
        }

        .step-indicator {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin-bottom: 24px;
        }

        .step {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 14px;
          transition: all 0.3s ease;
        }

        .step-active {
          background: #667eea;
          color: white;
        }

        .step-completed {
          background: #10b981;
          color: white;
        }

        .step-inactive {
          background: #e5e7eb;
          color: #9ca3af;
        }

        .step-line {
          width: 40px;
          height: 2px;
          background: #e5e7eb;
          transition: all 0.3s ease;
        }

        .step-line-completed {
          background: #10b981;
        }

        .loading-container {
          display: flex;
          justify-content: center;
          align-items: center;
          margin: 16px 0;
        }

        @media (max-width: 768px) {
          .forgot-input-field {
            height: 44px;
            padding: 0 12px 0 40px;
          }

          .forgot-input-icon {
            left: 12px;
          }

          .forgot-password-toggle {
            right: 12px;
          }

          .forgot-btn {
            height: 44px;
          }

          .info-container,
          .reset-container {
            padding: 16px;
          }

          .step {
            width: 28px;
            height: 28px;
            font-size: 12px;
          }

          .step-line {
            width: 30px;
          }
        }
      `}</style>

      <div className="forgot-password-container">
        <div className="step-indicator">
          <div className={`step ${!isCodeSent ? 'step-active' : 'step-completed'}`}>
            1
          </div>
          <div className={`step-line ${isCodeSent ? 'step-line-completed' : ''}`}></div>
          <div className={`step ${isCodeSent ? 'step-active' : 'step-inactive'}`}>
            2
          </div>
        </div>

        {!isCodeSent ? (
          <div>
            <div className="info-container">
              <AlertCircle size={24} className="info-icon" />
              <h3 className="info-title">Reset Your Password</h3>
              <p className="info-description">
                Enter your email address and we'll send you a verification code to reset your password.
              </p>
            </div>

            <div className="forgot-form-group">
              <div className="forgot-input-container">
                <input
                  className="forgot-input-field"
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <Mail className="forgot-input-icon" size={18} />
              </div>
            </div>

            <button
              className="forgot-btn"
              onClick={handleRequestCode}
              disabled={loadingRequest || !email}
            >
              {loadingRequest ? (
                <BeatLoader size={6} color="#ffffff" />
              ) : (
                <>
                  <Send size={18} />
                  Send Verification Code
                </>
              )}
            </button>
          </div>
        ) : (
          <div className="reset-container">
            <Key size={32} className="reset-icon" />
            <h3 className="reset-title">Enter New Password</h3>
            <p className="reset-description">
              We've sent a verification code to{' '}
              <span className="info-email">{email}</span>
            </p>

            <div className="forgot-form-group">
              <div className="forgot-input-container">
                <input
                  className="forgot-input-field"
                  type="text"
                  placeholder="Enter verification code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  required
                />
                <Key className="forgot-input-icon" size={18} />
              </div>
            </div>

            <div className="forgot-form-group">
              <div className="forgot-input-container">
                <input
                  className="forgot-input-field"
                  type={showNewPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
                <Lock className="forgot-input-icon" size={18} />
                <button
                  type="button"
                  className="forgot-password-toggle"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="forgot-form-group">
              <div className="forgot-input-container">
                <input
                  className="forgot-input-field"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
                <Lock className="forgot-input-icon" size={18} />
                <button
                  type="button"
                  className="forgot-password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              
              {(newPassword || confirmPassword) && (
                <div className="password-validation">
                  <div className={`validation-item ${passwordValid ? 'validation-success' : newPassword ? 'validation-error' : 'validation-neutral'}`}>
                    {passwordValid ? <CheckCircle size={12} /> : <AlertCircle size={12} />}
                    8+ characters
                  </div>
                  <div className={`validation-item ${passwordsMatch ? 'validation-success' : (newPassword && confirmPassword) ? 'validation-error' : 'validation-neutral'}`}>
                    {passwordsMatch ? <CheckCircle size={12} /> : <AlertCircle size={12} />}
                    Passwords match
                  </div>
                </div>
              )}
            </div>

            <button
              className="forgot-btn"
              onClick={handleResetPassword}
              disabled={loadingReset || !verificationCode || !newPassword || !confirmPassword || !passwordsMatch || !passwordValid}
            >
              {loadingReset ? (
                <BeatLoader size={6} color="#ffffff" />
              ) : (
                <>
                  <CheckCircle size={18} />
                  Reset Password
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default ForgotPassword;